/* istanbul ignore file */

import { Module } from 'vuex';

import { AppState } from '..';
import { FormField, SubmitOrderRequestPaymentMethod, DeliveryEstimate, IPaymentForm, TokenizedPaymentCard } from '@/models';
import { prefixValues, makeFormField, makeFormFieldOfType, formatPhoneNumber } from '@/helpers';
import cloneDeep from 'lodash/cloneDeep';
import { PaymentProviders } from '@/constants';
import { OrderAndHistoryMetricsRequest } from '@/models/sustainability';

const MUTATION_NAMES = {
  SET_SELECTED_PICKUP_TIME: 'setSelectedPickupTime',
  SET_AVAILABLE_PICKUP_TIMES: 'setAvailablePickupTimes',
  SET_PAYMENT_METHODS: 'setPaymentMethods',
  SET_PAYMENT_PROVIDER: 'setPaymentProvider',
  SET_ONE_TIME_PAYMENT_CARD: 'setOneTimePaymentCard',
  SET_DELIVERY_ESTIMATE: 'setDeliveryEstimate',
  SET_DELIVERY_TIP: 'setDeliveryTip',
  SET_IS_CUSTOM_TIP: 'setIsCustomTip',
  SET_DELIVERY_PHONE_NUMBER: 'setDeliveryPhoneNumber',
  SET_DELIVERY_NAME: 'setDeliveryName',
  SET_GUEST_INFO_PHONE_NUMBER: 'setGuestDeliveryPhoneNumber',
  SET_DELIVERY_INSTRUCTIONS: 'setDeliveryInstructions',
  SET_HAS_ESTIMATE_ERROR: 'setHasEstimateError',
  SET_ESTIMATE_ERROR_MESSAGE: 'setEstimateErrorMessage',
  SET_MINIMUM_DELIVERY_ORDER_VALUE: 'setMinimumDeliveryOrderValue',
  SET_IS_SUBMITTING_ORDER: 'setIsSubmittingOrder',
  SET_IS_DELIVERY_ESTIMATE_READY: 'setIsDeliveryEstimateReady',
  SET_FORCE_IN_STORE_PAYMENT: 'setForceInStorePayment',
  SET_PAYMENT_FROM: 'setPaymentForm',
  SET_IS_CARD_SAVED: 'setIsCardSaved',
  SET_SUSTAINABILITY_ERROR: 'setCheckoutSustainabilityError',
  SET_CREW_TIP: 'setCrewTip',
  SET_IS_CUSTOM_CREW_TIP: 'setIsCustomCrewTip',
  RESET_STATE: 'resetState',
};

export const MUTATIONS = prefixValues(MUTATION_NAMES, 'checkout/');

export interface OneTimePaymentForm {
  creditCardNumber: FormField;
  cvv: FormField;
  expirationDate: FormField;
  postalCode: FormField;
  cardholderName: FormField;
}

export interface GuestInfoForm {
  customerName: FormField;
  phoneNumber: FormField;
  email: FormField;
}

export interface CheckoutSustainabilityError {
  didError: boolean;
  postSustainabilityMetricsBody?: OrderAndHistoryMetricsRequest;
}

export interface CheckoutModuleState {
  isSubmittingOrder: boolean;
  paymentProvider: string;

  payment: {
    oneTimePaymentCard: TokenizedPaymentCard | null;
    methods: SubmitOrderRequestPaymentMethod[];
    oneTimePayment: OneTimePaymentForm;
    paymentForm?: IPaymentForm;
    rememberPaymentMethod: FormField<boolean>;
    forceInStorePayment: boolean;
    isCardSaved: boolean;
  };

  guest: {
    needsProfilePatch: boolean;
    info: GuestInfoForm;
  };

  customer: {};

  pickup: {
    availablePickupTimes: string[];
    pickupTime: FormField;
  };

  delivery: {
    tip: FormField<number>;
    isCustomTip: boolean;
    minimumOrderValue: number | null;
    estimate: DeliveryEstimate | null;
    hasEstimateError: boolean;
    estimateErrorMessage: string;
    instructions: FormField;
    isDeliveryEstimateReady: boolean;
    phoneNumber?: string;
    name?: string;
  };

  crewTip : {
    tip: FormField<number>;
    isCustomTip: boolean;
  }

  sustainability: {
    didError: boolean;
    postSustainabilityMetricsBody?: OrderAndHistoryMetricsRequest;
  };
}

const DEFAULT_STATE: CheckoutModuleState = {
  isSubmittingOrder: false,
  paymentProvider: PaymentProviders.Payeezy,
  payment: {
    oneTimePaymentCard: null,
    methods: [],
    oneTimePayment: {
      creditCardNumber: makeFormField(),
      cvv: makeFormField(),
      expirationDate: makeFormField(),
      postalCode: makeFormField(),
      cardholderName: makeFormField(),
    },
    rememberPaymentMethod: makeFormFieldOfType(false),
    forceInStorePayment: false,
    isCardSaved: false,
  },

  guest: {
    needsProfilePatch: true,
    info: {
      customerName: makeFormField(),
      phoneNumber: makeFormField(),
      email: makeFormField(),
    },
  },

  customer: {
    payment: {},
  },

  pickup: {
    availablePickupTimes: [],
    pickupTime: makeFormField(),
  },

  delivery: {
    tip: makeFormFieldOfType(0),
    isCustomTip: false,
    minimumOrderValue: null,
    estimate: null,
    hasEstimateError: false,
    estimateErrorMessage: '',
    instructions: makeFormField(),
    isDeliveryEstimateReady: false,
  },

  crewTip : {
    tip: makeFormFieldOfType(0),
    isCustomTip: false,
  },

  sustainability: {
    didError: false,
    postSustainabilityMetricsBody: undefined,
  },
};

export function makeCheckoutModule(): Module<CheckoutModuleState, AppState> {
  return {
    namespaced: true,
    state: cloneDeep(DEFAULT_STATE),
    mutations: {
      [MUTATION_NAMES.SET_SELECTED_PICKUP_TIME](state, time: string) {
        state.pickup.pickupTime.value = time;
      },
      [MUTATION_NAMES.SET_AVAILABLE_PICKUP_TIMES](state, availablePickupTimes: string[]) {
        state.pickup.availablePickupTimes = availablePickupTimes;
      },
      [MUTATION_NAMES.SET_PAYMENT_METHODS](state, methods: SubmitOrderRequestPaymentMethod[]) {
        state.payment.methods = methods;
      },
      [MUTATION_NAMES.SET_PAYMENT_PROVIDER](state, providerId: string) {
        state.paymentProvider = providerId;
      },
      [MUTATION_NAMES.SET_DELIVERY_ESTIMATE](state, estimate: DeliveryEstimate) {
        state.delivery.estimate = estimate;
      },
      [MUTATION_NAMES.SET_ONE_TIME_PAYMENT_CARD](state, card: TokenizedPaymentCard) {
        state.payment.oneTimePaymentCard = card;
      },
      [MUTATION_NAMES.SET_HAS_ESTIMATE_ERROR](state, hasEstimateError: boolean) {
        state.delivery.hasEstimateError = hasEstimateError;
      },
      [MUTATION_NAMES.SET_ESTIMATE_ERROR_MESSAGE](state, estimateErrorMessage: string) {
        state.delivery.estimateErrorMessage = estimateErrorMessage;
      },
      [MUTATION_NAMES.SET_DELIVERY_TIP](state, tip: number) {
        state.delivery.tip.value = tip;
      },
      [MUTATION_NAMES.SET_IS_CUSTOM_TIP](state, isCustomTip: boolean) {
        state.delivery.isCustomTip = isCustomTip;
      }, 
      [MUTATION_NAMES.SET_DELIVERY_PHONE_NUMBER](state, phone: string | undefined) {
        state.delivery.phoneNumber = phone;
      },
      [MUTATION_NAMES.SET_DELIVERY_NAME](state, name: string | undefined) {
        state.delivery.name = name;
      },
      [MUTATION_NAMES.SET_GUEST_INFO_PHONE_NUMBER](state, phone: string | undefined) {
        state.guest.info.phoneNumber = makeFormField(phone);
        if (phone) {
          state.guest.info.phoneNumber.formatted = formatPhoneNumber(phone);
        }
      },
      [MUTATION_NAMES.SET_DELIVERY_INSTRUCTIONS](state, instructions: string) {
        state.delivery.instructions.value = instructions;
      },
      [MUTATION_NAMES.SET_MINIMUM_DELIVERY_ORDER_VALUE](state, value: number) {
        state.delivery.minimumOrderValue = value;
      },
      [MUTATION_NAMES.SET_IS_SUBMITTING_ORDER](state, isSubmittingOrder: boolean) {
        state.isSubmittingOrder = isSubmittingOrder;
      },
      [MUTATION_NAMES.SET_IS_DELIVERY_ESTIMATE_READY](state, isDeliveryEstimateReady: boolean) {
        state.delivery.isDeliveryEstimateReady = isDeliveryEstimateReady;
      },
      [MUTATION_NAMES.SET_FORCE_IN_STORE_PAYMENT](state, force: boolean) {
        state.payment.forceInStorePayment = force;
      },
      [MUTATION_NAMES.SET_PAYMENT_FROM](state, paymentForm: IPaymentForm) {
        state.payment.paymentForm = paymentForm;
      },
      [MUTATION_NAMES.SET_IS_CARD_SAVED](state, isCardSaved: boolean) {
        state.payment.isCardSaved = isCardSaved;
      },
      [MUTATION_NAMES.RESET_STATE](state) {
        for (const prop of Object.keys(DEFAULT_STATE)) {
          (state as any)[prop] = cloneDeep((DEFAULT_STATE as any)[prop]);
        }
      },
      [MUTATION_NAMES.SET_SUSTAINABILITY_ERROR](state, susError: CheckoutSustainabilityError) {
        state.sustainability = susError;
      },
      [MUTATION_NAMES.SET_CREW_TIP](state, tip: number) {
        state.crewTip.tip.value = tip;
      },
      [MUTATION_NAMES.SET_IS_CUSTOM_CREW_TIP](state, isCustomTip: boolean) {
        state.crewTip.isCustomTip = isCustomTip;
      }, 
    },
  };
}
